import type { ChatStatusFrameOutput } from '@cotera/api';
import { Personality } from '../../etc/personality';

/** High-level status derived from stack frame traversal */
export type StackFrameAggregateStatus =
  | 'loading'
  | 'streaming'
  | 'error'
  | 'ready';

/**
 * Traverses stack frames and produces a high-level aggregate status.
 * - complete: deepest frame is completed or cancelled
 * - error: deepest frame is failed
 * - streaming: agent/stepChat actively generating (running)
 * - loading: queued, waiting, running tool, etc.
 *
 * Per-root "active" frames come from {@link getActiveFrame}. When any active is
 * cancelled and none are in-flight, we usually return `ready` — unless a
 * **newer** error-class or in-flight frame exists (resume then fail / resume),
 * in which case we surface `error` or `streaming` (see {@link pickRepresentativeStackFrame}).
 */
export function getStackFrameAggregateStatus(
  stackFrames: ChatStatusFrameOutput[] | null | undefined
): StackFrameAggregateStatus {
  const resolvedStackFrames = stackFrames ?? [];
  if (resolvedStackFrames.length === 0) {
    return 'ready';
  }

  const activeFrames = getActiveFramesForRoots(resolvedStackFrames);

  const cancelledActives = activeFrames.filter((f) => f.status === 'cancelled');
  const errorActives = activeFrames.filter(isErrorPriorityFrame);
  const inFlightActives = activeFrames.filter(isNonTerminalInFlightFrame);
  if (cancelledActives.length > 0) {
    if (inFlightActives.length === 0) {
      if (errorActives.length > 0) {
        const bestCancelled = bestFrameByLatestOwnActivity(cancelledActives);
        const bestErr = bestFrameByLatestOwnActivity(errorActives);
        if (
          latestSubtreeActivityMs(bestErr) <= latestActivityMs(bestCancelled)
        ) {
          return 'ready';
        }
        // Newer error after stale cancel — keep going (failed / streaming below)
      } else {
        return 'ready';
      }
    } else {
      const bestCancelled = bestFrameByLatestOwnActivity(cancelledActives);
      const bestInFlight = bestFrameByLatestOwnActivity(inFlightActives);
      if (
        latestSubtreeActivityMs(bestInFlight) <= latestActivityMs(bestCancelled)
      ) {
        return 'ready';
      }
    }
  }

  const failedActives = activeFrames.filter((f) => f.status === 'failed');
  if (failedActives.length > 0) {
    const completedActives = activeFrames.filter(
      (f) => f.status === 'completed'
    );
    if (completedActives.length === 0) {
      return 'error';
    }
    const bestFailed = bestFrameByLatestOwnActivity(failedActives);
    const bestCompleted = bestFrameByLatestSubtreeActivity(completedActives);
    if (
      latestSubtreeActivityMs(bestCompleted) <=
      latestSubtreeActivityMs(bestFailed)
    ) {
      return 'error';
    }
    // Newer completion supersedes stale failure — fall through to ready check
  }

  if (
    activeFrames.every(
      (f) => f.status === 'completed' || f.status === 'cancelled'
    )
  ) {
    return 'ready';
  }

  return 'streaming';
}

/** Distilled status keys that can be mapped to user-facing verbs */
export type StackFrameStatusKey =
  | 'idle'
  | 'thinking'
  | 'runningTool'
  | 'sleeping'
  | 'queued'
  | 'allocated'
  | 'retrying'
  | 'stalled'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'waiting';

/** Verbs for each distilled status - customize to change "personality" */
export type StackFrameStatusVerbs = Partial<
  Record<StackFrameStatusKey, string | string[]>
>;

function pickRandom(value: string | string[]): string {
  if (typeof value === 'string') {
    return value;
  }
  return value[Math.floor(Math.random() * value.length)]!;
}

const DEFAULT_VERBS: Required<Record<StackFrameStatusKey, string>> = {
  idle: '',
  thinking: 'Thinking',
  runningTool: 'Running tool',
  sleeping: 'Waiting',
  queued: 'Queued',
  allocated: 'Processing',
  retrying: 'Retrying',
  stalled: 'Stalled',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
  waiting: 'Waiting',
};

const WHIMSICAL_VERBS: StackFrameStatusVerbs = {
  thinking: [
    'Pondering the mysteries',
    'Noodling on this',
    'Hmm, let me think',
    'Mulling it over',
    'Brain gears spinning',
    'Deep in thought',
    'Cooking up ideas',
    'Chewing on this one',
    'Consulting my inner oracle',
    'Staring into the void for answers',
    'Putting on my thinking cap',
    'Connecting the dots',
    'Running the mental math',
    'Letting the ideas marinate',
    'Wrestling with this one',
    'Brainstorming away',
    'Sifting through the possibilities',
    'Following the thread',
    'Untangling my thoughts',
    'Turning this over in my mind',
  ],
  runningTool: [
    'Waving the magic wand',
    'Pulling levers',
    'Tinkering under the hood',
    'Pushing buttons',
    'Working the machinery',
    'Flipping switches',
    'Cranking the handle',
    'Doing the thing',
    'Firing up the widget',
    'Poking the API',
    'Feeding the hamster wheel',
    'Rattling the toolbox',
    'Summoning the data spirits',
    'Turning the crank',
    'Running the contraption',
    'Spinning up the gizmo',
    'Consulting the oracle',
    'Plugging things in',
    'Warming up the engine',
    'Jiggling the handle',
  ],
  sleeping: [
    'Counting sheep',
    'Taking a quick nap',
    'Resting my eyes',
    'On a little break',
    "Catching some z's",
    'Dozing off briefly',
    'Power napping',
    'Snoozing for a sec',
    'Recharging the batteries',
    'Taking five',
    'Having a little lie-down',
    'Waiting patiently',
    'Twiddling my thumbs',
    'Watching the clock',
    'Daydreaming a bit',
    'Cooling my jets',
    'Sitting tight',
    'In a holding pattern',
    'Letting the dust settle',
    'Pausing for dramatic effect',
  ],
  queued: [
    'In the queue',
    'Waiting my turn',
    'Standing in line',
    'Next up!',
    'Almost there',
    'Patiently queuing',
    'Holding my ticket',
    'In the waiting room',
    'On deck',
    'Warming the bench',
    'Queued up and ready',
    'Biding my time in line',
    'Taking a number',
    'In the pipeline',
    'On the runway',
    'Revving in the starting lane',
    'Inching forward',
    'Any minute now',
    'Waiting for the green light',
    'Lined up and eager',
  ],
  allocated: [
    "Gears a-turnin'",
    'Getting down to business',
    'Wheels in motion',
    'Off to the races',
    'Locked and loaded',
    'All systems go',
    'Firing on all cylinders',
    'Engines humming',
    'Picking up steam',
    'Rolling up my sleeves',
    'Getting into the groove',
    'Warming up nicely',
    'Hitting my stride',
    'Chugging along',
    'Making moves',
    'In the zone',
    'Pedal to the metal',
    'Full steam ahead',
    'On the case',
    'Dialed in and focused',
  ],
  retrying: [
    'Round two!',
    'Let me try that again',
    'Take two!',
    'One more time',
    'Dusting myself off',
    'Back at it',
    'Having another go',
    "Second time's the charm",
    'Giving it another shot',
    'Bouncing back',
    'Not giving up!',
    'Trying a different angle',
    'Regrouping and retrying',
    'Oops, let me redo that',
    'Shaking it off',
    'Rolling up my sleeves again',
    'Persistence is key',
    'Once more, with feeling',
    'Learning from my mistakes',
    'Third time lucky?',
  ],
  stalled: [
    'Catching my breath',
    'Hit a little snag',
    'Temporarily stuck',
    'Working through a hiccup',
    'Slight detour',
    'Untangling a knot',
    'Bear with me',
    'Bit of a roadblock',
    'Navigating a speed bump',
    'Taking a breather',
    'Regrouping for a moment',
    'Sorting out a kink',
    'Hold that thought',
    'Figuring out a puzzle piece',
    'Paused but not defeated',
    'Brief intermission',
    'Technical timeout',
    'Recalibrating',
    'Small bump in the road',
    'Gathering my wits',
  ],
  completed: [
    'Done and dusted',
    'All wrapped up!',
    'Mission accomplished',
    "And that's a wrap",
    'Finished!',
    'Voilà!',
    'Ta-da!',
    'All done here',
    'Nailed it',
    "Job's done",
    "That's that!",
    'Finito!',
    'And... scene!',
    'Wrapped up with a bow',
    'All tidied up',
    'Signed, sealed, delivered',
    'Check!',
    'Another one in the books',
    'There you go!',
    'Done deal',
  ],
  failed: [
    'Something went wrong',
    "Well, that didn't work",
    'Ran into trouble',
    "That didn't go as planned",
    'Hit a snag',
    'That was unexpected',
    'Things went sideways',
    'Back to the drawing board',
  ],
  cancelled: [
    'Dropped it',
    'Stopped here',
    'Called it off',
    'Scratched that',
    'Pulled the plug',
    'Shut it down',
    'Aborted mission',
    'Changed our minds',
    'Passed on it',
    'Stood down',
    'Wound it back',
    'Hit the brakes',
    'Cancelled',
    'Called it a day',
    'Backed out gracefully',
    'Packed it in',
    'Shelved this one',
    'Wrapped up early',
    'Moved on',
    'Had second thoughts',
  ],
  waiting: [
    'Biding my time',
    'Hanging tight',
    'Sitting pretty',
    'Holding steady',
    'Playing the waiting game',
    'Twiddling my thumbs',
    'Watching and waiting',
    'Standing by',
    'Keeping the seat warm',
    'In a holding pattern',
    'Eyes on the horizon',
    'Patiently hovering',
    'Keeping calm and waiting',
    'On standby',
    'Ready when you are',
    'Awaiting further instructions',
    'Chilling for a moment',
    'In position',
    'Ears perked, waiting',
    'Poised and ready',
  ],
};

const VERBS_BY_PERSONALITY: Record<Personality, StackFrameStatusVerbs> = {
  whimsical: WHIMSICAL_VERBS,
  professional: DEFAULT_VERBS,
};

export const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled'] as const;

export function isStackFrameStatusTerminal(status: string): boolean {
  return TERMINAL_STATUSES.includes(
    status as (typeof TERMINAL_STATUSES)[number]
  );
}

function isTerminal(status: string): boolean {
  return isStackFrameStatusTerminal(status);
}

/**
 * Get the active frame to display - the deepest non-terminal child, or the root.
 * When the last child is terminal: prefer showing cancelled/failed over the parent's completed.
 */
function getActiveFrame(frame: ChatStatusFrameOutput): ChatStatusFrameOutput {
  const children = frame.children ?? [];
  const lastChild = children.at(-1);
  if (lastChild !== undefined) {
    if (!isTerminal(lastChild.status)) {
      return getActiveFrame(lastChild);
    }
    // If parent is still non-terminal, keep showing parent progress instead of
    // surfacing a stale terminal child from an earlier attempt.
    if (!isTerminal(frame.status)) {
      return frame;
    }
    // Prefer showing cancelled/failed over parent's completed
    if (lastChild.status === 'cancelled' || lastChild.status === 'failed') {
      return lastChild;
    }
  }
  return frame;
}

/** One merged chatStatus payload can contain several roots (one per nested run). */
function getActiveFramesForRoots(
  stackFrames: ChatStatusFrameOutput[]
): ChatStatusFrameOutput[] {
  return stackFrames.map((root) => getActiveFrame(root));
}

/** Count how many retry events a frame has */
function countRetries(frame: ChatStatusFrameOutput): number {
  const events = frame.events ?? [];
  return events.filter((e) => e.eventType === 'retry').length;
}

/** Check whether the frame has had any error events (currently retrying) */
function hasErrorEvents(frame: ChatStatusFrameOutput): boolean {
  const events = frame.events ?? [];
  return events.some((e) => e.eventType === 'error');
}

/** Latest activity timestamp for this frame only (not descendants). */
function frameOwnLatestActivityAt(frame: ChatStatusFrameOutput): string {
  let max = frame.createdAt;
  for (const e of frame.events ?? []) {
    if (e.createdAt > max) {
      max = e.createdAt;
    }
  }
  return max;
}

function latestActivityMs(frame: ChatStatusFrameOutput): number {
  return Date.parse(frameOwnLatestActivityAt(frame));
}

/** Latest `createdAt` / event time anywhere in this frame’s subtree (inclusive). */
function frameSubtreeLatestActivityAt(frame: ChatStatusFrameOutput): string {
  let max = frameOwnLatestActivityAt(frame);
  for (const child of frame.children ?? []) {
    const childMax = frameSubtreeLatestActivityAt(child);
    if (childMax > max) {
      max = childMax;
    }
  }
  return max;
}

function latestSubtreeActivityMs(frame: ChatStatusFrameOutput): number {
  return Date.parse(frameSubtreeLatestActivityAt(frame));
}

function bestFrameByLatestOwnActivity(
  frames: ChatStatusFrameOutput[]
): ChatStatusFrameOutput {
  return frames.reduce((a, b) =>
    latestActivityMs(a) >= latestActivityMs(b) ? a : b
  );
}

function bestFrameByLatestSubtreeActivity(
  frames: ChatStatusFrameOutput[]
): ChatStatusFrameOutput {
  return frames.reduce((a, b) =>
    latestSubtreeActivityMs(a) >= latestSubtreeActivityMs(b) ? a : b
  );
}

/** Failed or non-terminal with error history (retry / waiting after error). */
function isErrorPriorityFrame(frame: ChatStatusFrameOutput): boolean {
  if (frame.status === 'failed') {
    return true;
  }
  if (!isTerminal(frame.status) && hasErrorEvents(frame)) {
    return true;
  }
  return false;
}

function isNonTerminalInFlightFrame(frame: ChatStatusFrameOutput): boolean {
  return !isTerminal(frame.status) && !isErrorPriorityFrame(frame);
}

function statusInfoFromChosenFrame(winner: ChatStatusFrameOutput): {
  statusKey: StackFrameStatusKey;
  isTerminal: boolean;
  isComplete: boolean;
  retryCount: number;
} {
  return {
    statusKey: getStatusKeyFromFrame(winner),
    isTerminal: isStackFrameStatusTerminal(winner.status),
    isComplete: winner.status === 'completed' || winner.status === 'cancelled',
    retryCount: countRetries(winner),
  };
}

/** Derive the status key from a frame without picking a random message */
function getStatusKeyFromFrame(
  activeFrame: ChatStatusFrameOutput
): StackFrameStatusKey {
  const status = activeFrame.status;
  const lastEvent = activeFrame.events?.at(-1);
  const lastEventType = lastEvent?.eventType;
  const fpType = activeFrame.fpType;

  if (lastEventType === 'retry') {
    return 'retrying';
  }

  // When status is 'waiting' (last event was 'error') and there have been
  // retries or errors, the frame is in a retry cycle - not just idly waiting.
  // Also applies to queued/allocated/running after a retry event.
  if (!isTerminal(status) && hasErrorEvents(activeFrame)) {
    return 'retrying';
  }

  switch (status) {
    case 'queued':
      return 'queued';
    case 'allocated':
      return 'allocated';
    case 'stalled':
      return 'stalled';
    case 'completed':
      return 'completed';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'cancelled';
    case 'waiting':
    case 'pending':
      return 'waiting';
    case 'running': {
      if (fpType === 'stepChat' || fpType === 'agent') {
        return 'thinking';
      }
      if (fpType === 'tool') {
        return 'runningTool';
      }
      if (fpType === 'sleep') {
        return 'sleeping';
      }
      return 'thinking';
    }
    default:
      return 'waiting';
  }
}

/**
 * The stack frame that drives status and {@link ChatStatusFrameOutput.lastError}
 * for the current chat, using the same rules as {@link getStackFrameStatusInfo}.
 */
export function pickRepresentativeStackFrame(
  stackFrames: ChatStatusFrameOutput[] | null | undefined
): ChatStatusFrameOutput | null {
  const resolvedStackFrames = stackFrames ?? [];
  if (resolvedStackFrames.length === 0) {
    return null;
  }

  const activeFrames = getActiveFramesForRoots(resolvedStackFrames);

  const cancelledActives = activeFrames.filter((f) => f.status === 'cancelled');
  const errorActives = activeFrames.filter(isErrorPriorityFrame);
  const inFlightActives = activeFrames.filter(isNonTerminalInFlightFrame);
  const completedActives = activeFrames.filter((f) => f.status === 'completed');

  if (cancelledActives.length > 0 && inFlightActives.length > 0) {
    const bestCancelled = bestFrameByLatestOwnActivity(cancelledActives);
    const bestInFlight = bestFrameByLatestOwnActivity(inFlightActives);
    if (
      latestSubtreeActivityMs(bestInFlight) <= latestActivityMs(bestCancelled)
    ) {
      return bestCancelled;
    }
    // Newer in-flight work after resume — ignore stale cancel for header / lastError
  } else if (cancelledActives.length > 0) {
    if (errorActives.length > 0) {
      const bestCancelled = bestFrameByLatestOwnActivity(cancelledActives);
      const bestErr = bestFrameByLatestOwnActivity(errorActives);
      if (latestSubtreeActivityMs(bestErr) <= latestActivityMs(bestCancelled)) {
        return bestCancelled;
      }
      // Newer error after resume — ignore stale cancel
    } else if (completedActives.length > 0) {
      // All active work is terminal (cancelled/completed). Reflect the latest
      // finished outcome instead of always pinning cancelled.
      return bestFrameByLatestSubtreeActivity([
        ...cancelledActives,
        ...completedActives,
      ]);
    } else {
      return bestFrameByLatestOwnActivity(cancelledActives);
    }
  }

  if (errorActives.length > 0 && inFlightActives.length > 0) {
    const bestErr = bestFrameByLatestOwnActivity(errorActives);
    const bestInFlight = bestFrameByLatestOwnActivity(inFlightActives);
    if (latestActivityMs(bestErr) >= latestActivityMs(bestInFlight)) {
      return bestErr;
    }
    return bestInFlight;
  }

  if (errorActives.length > 0) {
    // Don't surface a stale error when a newer completed frame supersedes it
    if (completedActives.length > 0) {
      const bestErr = bestFrameByLatestOwnActivity(errorActives);
      const bestCompleted = bestFrameByLatestSubtreeActivity(completedActives);
      if (
        latestSubtreeActivityMs(bestCompleted) >
        latestSubtreeActivityMs(bestErr)
      ) {
        return bestCompleted;
      }
    }
    return bestFrameByLatestOwnActivity(errorActives);
  }

  if (inFlightActives.length > 0) {
    return bestFrameByLatestOwnActivity(inFlightActives);
  }

  return bestFrameByLatestOwnActivity(activeFrames);
}

function normalizedLastError(frame: ChatStatusFrameOutput): string | undefined {
  const msg = frame.lastError;
  if (msg === undefined || msg === '') {
    return undefined;
  }
  return msg;
}

function hasNonEmptyLastError(frame: ChatStatusFrameOutput): boolean {
  return normalizedLastError(frame) !== undefined;
}

function collectFrames(frame: ChatStatusFrameOutput): ChatStatusFrameOutput[] {
  const children = frame.children ?? [];
  return [frame, ...children.flatMap((child) => collectFrames(child))];
}

function collectAllFrames(
  stackFrames: ChatStatusFrameOutput[]
): ChatStatusFrameOutput[] {
  return stackFrames.flatMap((frame) => collectFrames(frame));
}

function pickRepresentativeErrorFrame(
  stackFrames: ChatStatusFrameOutput[] | null | undefined
): ChatStatusFrameOutput | null {
  const resolvedStackFrames = stackFrames ?? [];
  if (resolvedStackFrames.length === 0) {
    return null;
  }

  const activeFrames = getActiveFramesForRoots(resolvedStackFrames);
  const activeWithError = activeFrames.filter(hasNonEmptyLastError);
  if (activeWithError.length > 0) {
    return bestFrameByLatestSubtreeActivity(activeWithError);
  }

  // Only surface historical descendant errors when the chat is otherwise
  // terminal. While any active frame is still in-flight, stale descendant
  // errors should not override current progress.
  const hasInFlightActive = activeFrames.some(
    (frame) => !isTerminal(frame.status)
  );
  if (hasInFlightActive) {
    return null;
  }

  const anyWithError =
    collectAllFrames(resolvedStackFrames).filter(hasNonEmptyLastError);
  if (anyWithError.length > 0) {
    return bestFrameByLatestOwnActivity(anyWithError);
  }

  return null;
}

export function getLastCancelledErrorFromStackFrames(
  stackFrames: ChatStatusFrameOutput[] | null | undefined
): string | undefined {
  const resolvedStackFrames = stackFrames ?? [];
  if (resolvedStackFrames.length === 0) {
    return undefined;
  }

  const activeFrames = getActiveFramesForRoots(resolvedStackFrames);

  const cancelledWithError = activeFrames.filter(
    (frame) => frame.status === 'cancelled' && hasNonEmptyLastError(frame)
  );
  if (cancelledWithError.length > 0) {
    return normalizedLastError(
      bestFrameByLatestSubtreeActivity(cancelledWithError)
    );
  }

  const hasInFlightActive = activeFrames.some(
    (frame) => !isTerminal(frame.status)
  );
  if (hasInFlightActive) {
    return undefined;
  }

  const historicalCancelledWithError = collectAllFrames(
    resolvedStackFrames
  ).filter(
    (frame) => frame.status === 'cancelled' && hasNonEmptyLastError(frame)
  );
  if (historicalCancelledWithError.length === 0) {
    return undefined;
  }

  return normalizedLastError(
    bestFrameByLatestOwnActivity(historicalCancelledWithError)
  );
}

/**
 * Get structural status info from stack frames without picking a random message.
 *
 * Picks the most important status across all roots and nested children:
 * 1. Cancellation vs non-error in-flight — when both exist, prefer cancelled
 *    only if its latest own activity is at least as recent as the best in-flight
 *    frame’s **subtree** latest activity (so a resumed run with fresh child work
 *    beats a stale cancel even when the active agent row’s own events are old).
 * 2. Otherwise, if only cancelled actives remain (no in-flight), pick cancelled
 *    unless an error-class frame is **strictly newer** (resume then fail).
 * 3. If both error-class frames (failed / retrying after errors) and other
 *    in-flight frames exist, prefer the error class only when its latest activity
 *    is at least as recent as the in-flight frames.
 * 4. Otherwise prefer any remaining error-class frame, then any other non-terminal
 *    in-flight frame, by most recent own activity within that bucket.
 * 5. Otherwise all actives are terminal — pick the most recently active one.
 *
 * "Active" per root is from {@link getActiveFrame} (deepest in-progress work,
 * with the same parent/child rules as before).
 *
 * When the server has resolved {@link ChatStatusFrameOutput.lastError} from
 * error-event metadata, it is attached here for the representative frame.
 */
export function getStackFrameStatusInfo(
  stackFrames: ChatStatusFrameOutput[] | null | undefined
): {
  statusKey: StackFrameStatusKey;
  isTerminal: boolean;
  isComplete: boolean;
  retryCount: number;
  lastError?: string;
} {
  const resolvedStackFrames = stackFrames ?? [];
  if (resolvedStackFrames.length === 0) {
    return {
      statusKey: 'idle',
      isTerminal: true,
      isComplete: true,
      retryCount: 0,
      lastError: undefined,
    };
  }

  const winner = pickRepresentativeStackFrame(resolvedStackFrames);
  if (winner === null) {
    return {
      statusKey: 'idle',
      isTerminal: true,
      isComplete: true,
      retryCount: 0,
      lastError: undefined,
    };
  }

  const errorWinner = pickRepresentativeErrorFrame(resolvedStackFrames);

  return {
    ...statusInfoFromChosenFrame(winner),
    lastError:
      errorWinner !== null
        ? normalizedLastError(errorWinner)
        : normalizedLastError(winner),
  };
}

/**
 * Pick a single random message for a given status key and personality.
 */
export function pickMessageForKey(
  statusKey: StackFrameStatusKey,
  personality: Personality
): string {
  const personalityVerbs = VERBS_BY_PERSONALITY[personality][statusKey];
  if (personalityVerbs !== undefined) {
    return pickRandom(personalityVerbs);
  }
  return DEFAULT_VERBS[statusKey];
}

/**
 * Human-readable error for the representative stack frame (same selection as
 * {@link getStackFrameStatusInfo}), when the server set {@link ChatStatusFrameOutput.lastError}
 * from error-event KV metadata.
 */
export function getLastErrorFromStackFrames(
  stackFrames: ChatStatusFrameOutput[] | null | undefined
): string | undefined {
  const errorWinner = pickRepresentativeErrorFrame(stackFrames);
  if (errorWinner === null) {
    return undefined;
  }
  return normalizedLastError(errorWinner);
}

/**
 * Recursively sums `credits` across a tree of stack frames.
 */
export function sumCreditsFromFrameTree(
  frames: ChatStatusFrameOutput[]
): number {
  let total = 0;
  for (const frame of frames) {
    total += frame.credits;
    total += sumCreditsFromFrameTree(frame.children ?? []);
  }
  return total;
}

/**
 * Get the status message, whether the current state is terminal, and whether complete.
 * Use isTerminal to decide whether to show animated ellipsis.
 * isComplete is true when status is completed or cancelled (not failed).
 */
export function getStackFrameStatusWithTerminal(
  stackFrames: ChatStatusFrameOutput[],
  personality: Personality
): { message: string; isTerminal: boolean; isComplete: boolean } {
  const { statusKey, isTerminal, isComplete } =
    getStackFrameStatusInfo(stackFrames);
  const message = pickMessageForKey(statusKey, personality);

  return { message, isTerminal, isComplete };
}
